/**
 * Production Environment Configuration
 * Handles production-specific settings, error tracking, and optimizations
 */

export interface ProductionConfig {
  api: {
    baseUrl: string;
    timeout: number;
    retryAttempts: number;
    retryDelay: number;
  };
  app: {
    name: string;
    version: string;
    environment: string;
  };
  performance: {
    enableMonitoring: boolean;
    sampleRate: number;
    bundleAnalyzer: boolean;
    preloadCriticalResources: boolean;
  };
  security: {
    enableCSP: boolean;
    enableHSTS: boolean;
    secureCookies: boolean;
    enableSecurityHeaders: boolean;
  };
  errorTracking: {
    enabled: boolean;
    sampleRate: number;
    enablePerformanceTracking: boolean;
    enableUserFeedback: boolean;
  };
  analytics: {
    enabled: boolean;
    sampleRate: number;
    trackUserInteractions: boolean;
    trackPerformanceMetrics: boolean;
  };
  pwa: {
    updateStrategy: 'prompt' | 'auto';
    cacheStrategy: 'networkFirst' | 'cacheFirst' | 'staleWhileRevalidate';
    offlineFallback: boolean;
    backgroundSync: boolean;
  };
  logging: {
    level: 'error' | 'warn' | 'info' | 'debug';
    enableConsoleLogs: boolean;
    enableRemoteLogging: boolean;
    retentionDays: number;
  };
  features: {
    enableBetaFeatures: boolean;
    enableDebugMode: boolean;
    enableMaintenanceMode: boolean;
    enableABTesting: boolean;
  };
  cache: {
    version: string;
    enableAggressiveCaching: boolean;
    durationStatic: number;
    durationAPI: number;
    durationImages: number;
  };
  cdn: {
    baseUrl?: string;
    enabled: boolean;
    fallback: boolean;
  };
  thirdParty: {
    enableSentry: boolean;
    enableHotjar: boolean;
    enableGoogleAnalytics: boolean;
  };
}

/**
 * Get production configuration from environment variables
 */
export function getProductionConfig(): ProductionConfig {
  return {
    api: {
      baseUrl: import.meta.env.VITE_API_BASE_URL || 'https://www.tarot-app.online',
      timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 15000,
      retryAttempts: parseInt(import.meta.env.VITE_API_RETRY_ATTEMPTS) || 3,
      retryDelay: parseInt(import.meta.env.VITE_API_RETRY_DELAY) || 1000,
    },
    app: {
      name: import.meta.env.VITE_APP_NAME || 'Tarot Card Reader',
      version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      environment: import.meta.env.VITE_APP_ENVIRONMENT || 'production',
    },
    performance: {
      enableMonitoring: import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING === 'true',
      sampleRate: parseFloat(import.meta.env.VITE_PERFORMANCE_SAMPLE_RATE) || 0.1,
      bundleAnalyzer: import.meta.env.VITE_BUNDLE_ANALYZER === 'true',
      preloadCriticalResources: import.meta.env.VITE_PRELOAD_CRITICAL_RESOURCES === 'true',
    },
    security: {
      enableCSP: import.meta.env.VITE_ENABLE_CSP === 'true',
      enableHSTS: import.meta.env.VITE_ENABLE_HSTS === 'true',
      secureCookies: import.meta.env.VITE_SECURE_COOKIES === 'true',
      enableSecurityHeaders: import.meta.env.VITE_ENABLE_SECURITY_HEADERS === 'true',
    },
    errorTracking: {
      enabled: import.meta.env.VITE_ENABLE_ERROR_TRACKING === 'true',
      sampleRate: parseFloat(import.meta.env.VITE_ERROR_SAMPLE_RATE) || 1.0,
      enablePerformanceTracking: import.meta.env.VITE_ENABLE_PERFORMANCE_TRACKING === 'true',
      enableUserFeedback: import.meta.env.VITE_ENABLE_USER_FEEDBACK === 'true',
    },
    analytics: {
      enabled: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
      sampleRate: parseFloat(import.meta.env.VITE_ANALYTICS_SAMPLE_RATE) || 1.0,
      trackUserInteractions: import.meta.env.VITE_TRACK_USER_INTERACTIONS === 'true',
      trackPerformanceMetrics: import.meta.env.VITE_TRACK_PERFORMANCE_METRICS === 'true',
    },
    pwa: {
      updateStrategy: (import.meta.env.VITE_PWA_UPDATE_STRATEGY as 'prompt' | 'auto') || 'prompt',
      cacheStrategy: (import.meta.env.VITE_PWA_CACHE_STRATEGY as any) || 'networkFirst',
      offlineFallback: import.meta.env.VITE_PWA_OFFLINE_FALLBACK === 'true',
      backgroundSync: import.meta.env.VITE_PWA_BACKGROUND_SYNC === 'true',
    },
    logging: {
      level: (import.meta.env.VITE_LOG_LEVEL as any) || 'warn',
      enableConsoleLogs: import.meta.env.VITE_ENABLE_CONSOLE_LOGS === 'true',
      enableRemoteLogging: import.meta.env.VITE_ENABLE_REMOTE_LOGGING === 'true',
      retentionDays: parseInt(import.meta.env.VITE_LOG_RETENTION_DAYS) || 30,
    },
    features: {
      enableBetaFeatures: import.meta.env.VITE_ENABLE_BETA_FEATURES === 'true',
      enableDebugMode: import.meta.env.VITE_ENABLE_DEBUG_MODE === 'true',
      enableMaintenanceMode: import.meta.env.VITE_ENABLE_MAINTENANCE_MODE === 'true',
      enableABTesting: import.meta.env.VITE_ENABLE_A_B_TESTING === 'true',
    },
    cache: {
      version: import.meta.env.VITE_CACHE_VERSION || 'v1.0.0',
      enableAggressiveCaching: import.meta.env.VITE_ENABLE_AGGRESSIVE_CACHING === 'true',
      durationStatic: parseInt(import.meta.env.VITE_CACHE_DURATION_STATIC) || 2592000, // 30 days
      durationAPI: parseInt(import.meta.env.VITE_CACHE_DURATION_API) || 300, // 5 minutes
      durationImages: parseInt(import.meta.env.VITE_CACHE_DURATION_IMAGES) || 86400, // 1 day
    },
    cdn: {
      baseUrl: import.meta.env.VITE_CDN_BASE_URL,
      enabled: import.meta.env.VITE_ENABLE_CDN === 'true',
      fallback: import.meta.env.VITE_CDN_FALLBACK === 'true',
    },
    thirdParty: {
      enableSentry: import.meta.env.VITE_ENABLE_SENTRY === 'true',
      enableHotjar: import.meta.env.VITE_ENABLE_HOTJAR === 'true',
      enableGoogleAnalytics: import.meta.env.VITE_ENABLE_GOOGLE_ANALYTICS === 'true',
    },
  };
}

/**
 * Check if we're in production environment
 */
export function isProduction(): boolean {
  return import.meta.env.MODE === 'production' || 
         import.meta.env.VITE_APP_ENVIRONMENT === 'production';
}

/**
 * Check if a feature is enabled
 */
export function isFeatureEnabled(feature: keyof ProductionConfig['features']): boolean {
  const config = getProductionConfig();
  return config.features[feature];
}

/**
 * Get CDN URL for assets
 */
export function getCDNUrl(path: string): string {
  const config = getProductionConfig();
  
  if (config.cdn.enabled && config.cdn.baseUrl) {
    return `${config.cdn.baseUrl}${path}`;
  }
  
  return path;
}

/**
 * Get cache duration for resource type
 */
export function getCacheDuration(type: 'static' | 'api' | 'images'): number {
  const config = getProductionConfig();
  
  switch (type) {
    case 'static':
      return config.cache.durationStatic;
    case 'api':
      return config.cache.durationAPI;
    case 'images':
      return config.cache.durationImages;
    default:
      return config.cache.durationAPI;
  }
}

/**
 * Validate production configuration
 */
export function validateProductionConfig(): { valid: boolean; errors: string[] } {
  const config = getProductionConfig();
  const errors: string[] = [];
  
  // Validate required fields
  if (!config.api.baseUrl) {
    errors.push('API base URL is required');
  }
  
  if (isProduction()) {
    // Production-specific validations
    if (!config.api.baseUrl.startsWith('https://')) {
      errors.push('API base URL must use HTTPS in production');
    }
    
    if (config.api.baseUrl.includes('localhost') || config.api.baseUrl.includes('127.0.0.1')) {
      errors.push('API base URL cannot point to localhost in production');
    }
    
    if (config.logging.enableConsoleLogs) {
      errors.push('Console logs should be disabled in production');
    }
    
    if (config.features.enableDebugMode) {
      errors.push('Debug mode should be disabled in production');
    }
    
    if (!config.security.enableCSP) {
      errors.push('Content Security Policy should be enabled in production');
    }
    
    if (!config.security.enableHSTS) {
      errors.push('HSTS should be enabled in production');
    }
    
    if (!config.security.secureCookies) {
      errors.push('Secure cookies should be enabled in production');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Log production configuration (safe for logging - no sensitive data)
 */
export function logProductionConfig(): void {
  const config = getProductionConfig();
  
  console.log('Production Configuration:', {
    app: {
      name: config.app.name,
      version: config.app.version,
      environment: config.app.environment,
    },
    api: {
      baseUrl: config.api.baseUrl,
      timeout: config.api.timeout,
    },
    features: {
      errorTracking: config.errorTracking.enabled,
      analytics: config.analytics.enabled,
      performance: config.performance.enableMonitoring,
      pwa: config.pwa,
    },
    security: {
      csp: config.security.enableCSP,
      hsts: config.security.enableHSTS,
      secureCookies: config.security.secureCookies,
    },
    cache: {
      version: config.cache.version,
      aggressive: config.cache.enableAggressiveCaching,
    },
  });
}

/**
 * Production configuration singleton
 */
export const productionConfig = getProductionConfig();